import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { firestore, storage, auth } from "../firebase";
import firebase from "../firebase";
import useAuth from "../hooks/useAuth";
import TopBar from "./TopBar";
import Modal from "./Modal";
import { useModal } from "../hooks/useModal";
import { Helmet } from "react-helmet";
import { FiUpload, FiImage, FiBold, FiItalic, FiUnderline, FiList, FiAlignLeft, FiAlignCenter, FiAlignRight, FiType } from "react-icons/fi";
import "../styles/quotation-system.css";

export default function BlogAdmin() {
  const history = useHistory();
  const { user, profile } = useAuth();
  const { modal, showAlert, showConfirm, hideModal } = useModal();
  const [blogs, setBlogs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [viewingBlog, setViewingBlog] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [publishingBlogId, setPublishingBlogId] = useState(null);
  const [sendingBackBlogId, setSendingBackBlogId] = useState(null);
  
  // Form state
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [body, setBody] = useState("");
  const [heroImage, setHeroImage] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState("");
  const [heroImageUrl, setHeroImageUrl] = useState("");
  const [sections, setSections] = useState({
    commercial: false,
    residential: false,
    industrial: false
  });
  const [excerpt, setExcerpt] = useState("");
  
  // Formatting state tracking
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    underline: false,
    fontSize: '16px',
    textStyle: '',
    alignment: '',
    color: '#000000'
  });
  
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  const bodyEditorRef = useRef(null);
  const imageInputRef = useRef(null);
  const colorPickerRef = useRef(null);

  useEffect(() => {
    if (!user || (profile?.role !== "admin" && profile?.role !== "contentwriter")) {
      history.push("/login");
      return;
    }

    // Listen for create new blog event from TopBar
    const handleCreateNewBlog = () => {
      if (!isEditing) {
        setIsEditing(true);
        setEditingBlog(null);
        setTitle("");
        setSubtitle("");
        setBody("");
        setExcerpt("");
        setHeroImage(null);
        setHeroImagePreview("");
        setHeroImageUrl("");
        setSections({ commercial: false, residential: false, industrial: false });
        window.scrollTo(0, 0);
        // Clear editor content after a brief delay
        setTimeout(() => {
          if (bodyEditorRef.current) {
            bodyEditorRef.current.innerHTML = "";
          }
        }, 100);
      }
    };
    window.addEventListener('createNewBlog', handleCreateNewBlog);

    // Real-time listener - changes appear immediately without redeployment
    // Admins see all blogs, content writers see only their own blogs
    console.log("Setting up blog listener for:", {
      role: profile?.role,
      profileName: profile?.name,
      userId: user?.uid
    });
    
    const unsub = firestore
      .collection("blogs")
      .onSnapshot(
        (snap) => {
          console.log("Firestore snapshot received:", {
            role: profile?.role,
            profileName: profile?.name,
            docCount: snap.docs.length,
            hasPendingWrites: snap.metadata.hasPendingWrites
          });
          
          const allBlogsData = snap.docs.map((d) => {
            const data = d.data();
            const blogData = { 
              id: d.id, 
              ...data,
              createdAt: data.createdAt || firebase.firestore.Timestamp.now()
            };
            return blogData;
          });
          
          console.log("All blogs from Firestore:", allBlogsData.map(b => ({
            id: b.id,
            title: b.title,
            author: b.author,
            published: b.published,
            pendingApproval: b.pendingApproval
          })));
          
          // Debug: Log all blogs and profile info
          if (profile?.role === "contentwriter" || profile?.role === "admin") {
            console.log(`${profile?.role} Debug:`, {
              profileName: profile?.name,
              totalBlogs: allBlogsData.length,
              allBlogs: allBlogsData.map(b => ({
                id: b.id,
                title: b.title,
                author: b.author,
                published: b.published,
                pendingApproval: b.pendingApproval
              }))
            });
          }
          
          // Filter: content writers only see their own blogs
          const blogsData = allBlogsData.filter(blog => {
            if (profile?.role === "admin") return true;
            if (profile?.role === "contentwriter") {
              // Content writers see their own blogs (published or unpublished)
              // Match by author name (case-insensitive and trimmed)
              const blogAuthor = (blog.author || "").trim().toLowerCase();
              const profileName = (profile?.name || "").trim().toLowerCase();
              const matches = blogAuthor === profileName;
              
              // Debug logging
              console.log("Blog filter check:", {
                blogId: blog.id,
                blogTitle: blog.title,
                blogAuthor,
                profileName,
                matches,
                published: blog.published
              });
              
              return matches;
            }
            return false;
          });
          
          if (profile?.role === "contentwriter") {
            console.log("Filtered blogs for content writer:", {
              count: blogsData.length,
              blogs: blogsData.map(b => ({
                id: b.id,
                title: b.title,
                published: b.published
              }))
            });
          }
          
          if (profile?.role === "admin") {
            console.log("Admin - All blogs (no filter):", {
              total: blogsData.length,
              published: blogsData.filter(b => b.published).length,
              pending: blogsData.filter(b => !b.published).length
            });
          }
          
          // Sort manually by createdAt (newest first)
          blogsData.sort((a, b) => {
            const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds * 1000) || 0;
            const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds * 1000) || 0;
            return bTime - aTime;
          });
          setBlogs(blogsData);
        },
        (error) => {
          console.error("Error fetching blogs:", error);
          // If there's a permission error, show a helpful message
          if (error.code === 'permission-denied') {
            showAlert("Permission denied. Please make sure you're logged in and that Firestore rules have been deployed.", "error", "Permission Error");
          }
        }
      );
    
    return () => {
      window.removeEventListener('createNewBlog', handleCreateNewBlog);
      unsub();
    };
  }, [user, profile, history]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showAlert("Image size must be less than 5MB", "warning", "File Too Large");
      return;
    }

    setHeroImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setHeroImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!heroImage) return heroImageUrl;

    try {
      // Sanitize filename to avoid special characters that might cause issues
      const sanitizedName = heroImage.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storageRef = storage.ref();
      const imagePath = `blog-images/${Date.now()}_${sanitizedName}`;
      const imageRef = storageRef.child(imagePath);
      
      // Upload with metadata
      const metadata = {
        contentType: heroImage.type || 'image/jpeg',
        customMetadata: {
          uploadedBy: profile?.name || 'Unknown',
          uploadedAt: new Date().toISOString()
        }
      };
      
      const snapshot = await imageRef.put(heroImage, metadata);
      const url = await snapshot.ref.getDownloadURL();
      console.log('Image uploaded successfully:', url);
      return url;
    } catch (error) {
      console.error("Error uploading image:", error);
      // Provide more helpful error messages
      if (error.code === 'storage/unauthorized') {
        throw new Error('You do not have permission to upload images. Please contact an admin.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload was canceled.');
      } else if (error.code === 'storage/unknown') {
        throw new Error('An unknown error occurred during upload. Please check your internet connection and try again.');
      }
      throw error;
    }
  };

  const formatText = (command, value = null) => {
    if (!bodyEditorRef.current) return;
    
    // Focus the editor first
    bodyEditorRef.current.focus();
    
    // Save current selection
    const selection = window.getSelection();
    let range = null;
    
    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else {
      // Create a new range at the end if no selection
      range = document.createRange();
      range.selectNodeContents(bodyEditorRef.current);
      range.collapse(false); // Collapse to end
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    // Execute command
    try {
      const success = document.execCommand(command, false, value);
      if (success) {
        // Update body state after a short delay to ensure DOM is updated
        setTimeout(() => {
          setBody(bodyEditorRef.current.innerHTML);
          checkFormatState();
          // Refocus to maintain cursor position
          bodyEditorRef.current.focus();
        }, 10);
      }
    } catch (e) {
      console.error('Error executing command:', e);
    }
  };

  const formatColor = (color) => {
    if (!bodyEditorRef.current) return;
    
    bodyEditorRef.current.focus();
    const selection = window.getSelection();
    let range = null;
    
    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(bodyEditorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    try {
      if (range && !range.collapsed) {
        // If text is selected, apply color
        formatText('foreColor', color);
      } else {
        // Insert a span for next typed text
        const span = document.createElement('span');
        span.style.color = color;
        range.insertNode(span);
        range.setStart(span, 0);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        setTimeout(() => {
          setBody(bodyEditorRef.current.innerHTML);
          bodyEditorRef.current.focus();
        }, 10);
      }
    } catch (e) {
      console.error('Error setting color:', e);
      formatText('foreColor', color);
    }
  };

  const formatHeading = (level) => {
    if (!bodyEditorRef.current) return;
    
    bodyEditorRef.current.focus();
    const selection = window.getSelection();
    
    if (selection.rangeCount === 0) {
      // If no selection, create a new heading at the cursor
      const heading = document.createElement(`h${level}`);
      heading.style.fontSize = level === '1' ? '32px' : level === '2' ? '28px' : level === '3' ? '24px' : '20px';
      heading.style.fontWeight = (level === '1' || level === '2') ? '700' : '600';
      heading.style.color = '#1F3A93';
      heading.style.marginTop = level === '1' || level === '2' ? '2.5rem' : level === '3' ? '2rem' : '1.5rem';
      heading.style.marginBottom = level === '1' || level === '2' ? '1.5rem' : level === '3' ? '1rem' : '0.75rem';
      heading.style.lineHeight = '1.3';
      heading.style.fontFamily = "'Poppins', sans-serif";
      heading.innerHTML = '<br>';
      
      const range = document.createRange();
      range.selectNodeContents(bodyEditorRef.current);
      range.collapse(false);
      range.insertNode(heading);
      range.setStart(heading, 0);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      
      setTimeout(() => {
        setBody(bodyEditorRef.current.innerHTML);
        bodyEditorRef.current.focus();
      }, 10);
      return;
    }
    
    const range = selection.getRangeAt(0);
    
    try {
      // Check if selection is already in a heading
      let container = range.commonAncestorContainer;
      if (container.nodeType === Node.TEXT_NODE) {
        container = container.parentElement;
      }
      
      // If already a heading, just update its level and styles
      if (container && container.tagName && container.tagName.match(/^H[1-6]$/)) {
        const newHeading = document.createElement(`h${level}`);
        newHeading.style.fontSize = level === '1' ? '32px' : level === '2' ? '28px' : level === '3' ? '24px' : '20px';
        newHeading.style.fontWeight = (level === '1' || level === '2') ? '700' : '600';
        newHeading.style.color = '#1F3A93';
        newHeading.style.marginTop = level === '1' || level === '2' ? '2.5rem' : level === '3' ? '2rem' : '1.5rem';
        newHeading.style.marginBottom = level === '1' || level === '2' ? '1.5rem' : level === '3' ? '1rem' : '0.75rem';
        newHeading.style.lineHeight = '1.3';
        newHeading.style.fontFamily = "'Poppins', sans-serif";
        newHeading.innerHTML = container.innerHTML;
        container.parentNode.replaceChild(newHeading, container);
        
        setTimeout(() => {
          setBody(bodyEditorRef.current.innerHTML);
          bodyEditorRef.current.focus();
        }, 10);
        return;
      }
      
      // Use formatBlock to create the heading
      const success = document.execCommand('formatBlock', false, `h${level}`);
      
      if (success) {
        // Apply styles to all headings of this level in the editor
        setTimeout(() => {
          const headings = bodyEditorRef.current.querySelectorAll(`h${level}`);
          headings.forEach(heading => {
            heading.style.fontSize = level === '1' ? '32px' : level === '2' ? '28px' : level === '3' ? '24px' : '20px';
            heading.style.fontWeight = (level === '1' || level === '2') ? '700' : '600';
            heading.style.color = '#1F3A93';
            heading.style.marginTop = level === '1' || level === '2' ? '2.5rem' : level === '3' ? '2rem' : '1.5rem';
            heading.style.marginBottom = level === '1' || level === '2' ? '1.5rem' : level === '3' ? '1rem' : '0.75rem';
            heading.style.lineHeight = '1.3';
            heading.style.fontFamily = "'Poppins', sans-serif";
          });
          
          setBody(bodyEditorRef.current.innerHTML);
          bodyEditorRef.current.focus();
        }, 50);
      } else {
        // Fallback: manually wrap selection in heading
        const contents = range.extractContents();
        const heading = document.createElement(`h${level}`);
        heading.style.fontSize = level === '1' ? '32px' : level === '2' ? '28px' : level === '3' ? '24px' : '20px';
        heading.style.fontWeight = (level === '1' || level === '2') ? '700' : '600';
        heading.style.color = '#1F3A93';
        heading.style.marginTop = level === '1' || level === '2' ? '2.5rem' : level === '3' ? '2rem' : '1.5rem';
        heading.style.marginBottom = level === '1' || level === '2' ? '1.5rem' : level === '3' ? '1rem' : '0.75rem';
        heading.style.lineHeight = '1.3';
        heading.style.fontFamily = "'Poppins', sans-serif";
        heading.appendChild(contents);
        range.insertNode(heading);
        
        setTimeout(() => {
          setBody(bodyEditorRef.current.innerHTML);
          bodyEditorRef.current.focus();
        }, 10);
      }
    } catch (e) {
      console.error('Error formatting heading:', e);
    }
  };

  const formatFontSize = (size) => {
    if (!bodyEditorRef.current) return;
    
    bodyEditorRef.current.focus();
    const selection = window.getSelection();
    let range = null;
    
    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(bodyEditorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    try {
      // Create a span with the font size
      const span = document.createElement('span');
      span.style.fontSize = size;
      
      if (!range.collapsed) {
        // If text is selected, wrap it
        try {
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        } catch (e) {
          // Fallback: use execCommand
          document.execCommand('fontSize', false, '7');
          const fontElements = bodyEditorRef.current.querySelectorAll('font[size="7"]');
          fontElements.forEach(el => {
            const newSpan = document.createElement('span');
            newSpan.style.fontSize = size;
            newSpan.innerHTML = el.innerHTML;
            el.parentNode.replaceChild(newSpan, el);
          });
        }
      } else {
        // Insert span for next typed text
        range.insertNode(span);
        range.setStartAfter(span);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      setTimeout(() => {
        setBody(bodyEditorRef.current.innerHTML);
        bodyEditorRef.current.focus();
      }, 10);
    } catch (e) {
      console.error('Error setting font size:', e);
    }
  };

  const handleSaveDraft = async (e) => {
    e.preventDefault();
    
    // Drafts don't require all fields - only title is required
    if (!title.trim()) {
      showAlert("Please enter a title for your draft", "warning", "Required Field");
      return;
    }

    setIsSavingDraft(true);

    try {
      let finalImageUrl = heroImageUrl || editingBlog?.heroImage;
      
      if (heroImage) {
        try {
          finalImageUrl = await uploadImage();
        } catch (uploadError) {
          console.error("Image upload failed:", uploadError);
          showAlert(`Failed to upload image: ${uploadError.message || uploadError}\n\nPlease try again or use a direct image URL instead.`, "error", "Upload Failed");
          setIsSavingDraft(false);
          return;
        }
      }

      const selectedSections = [];
      if (sections.commercial) selectedSections.push("commercial");
      if (sections.residential) selectedSections.push("residential");
      if (sections.industrial) selectedSections.push("industrial");

      // Get body content from editor if not set
      let finalBody = body;
      if (bodyEditorRef.current) {
        finalBody = bodyEditorRef.current.innerHTML || body;
      }

      const blogData = {
        title: title.trim(),
        subtitle: subtitle.trim() || "",
        body: finalBody || "",
        excerpt: excerpt.trim() || subtitle.trim() || "",
        heroImage: finalImageUrl || "",
        sections: selectedSections,
        author: profile?.name || "Lunar Electric",
        createdAt: editingBlog?.createdAt || firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        published: false,
        slug: editingBlog?.slug || generateSlug(title),
        pendingApproval: false, // Drafts are not sent for approval
        draft: true, // Mark as draft
        sentBackForRevision: false // Clear sent back flag if it was a sent back blog
      };

      if (editingBlog) {
        await firestore.collection("blogs").doc(editingBlog.id).update(blogData);
      } else {
        await firestore.collection("blogs").add(blogData);
      }

      // Reset form
      setTitle("");
      setSubtitle("");
      setBody("");
      setExcerpt("");
      setHeroImage(null);
      setHeroImagePreview("");
      setHeroImageUrl("");
      setSections({ commercial: false, residential: false, industrial: false });
      setIsEditing(false);
      setEditingBlog(null);
      
      showAlert(
        editingBlog
          ? "Draft saved successfully! You can continue editing it later." 
          : "Draft saved successfully! You can continue working on it later.",
        "success",
        "Draft Saved"
      );
    } catch (error) {
      console.error("Error saving draft:", error);
      showAlert("Error saving draft. Please try again.", "error", "Error");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !subtitle.trim()) {
      showAlert("Please fill in title and subtitle", "warning", "Required Fields");
      return;
    }

    if (!sections.commercial && !sections.residential && !sections.industrial) {
      showAlert("Please select at least one section", "warning", "Required Fields");
      return;
    }

    if (!heroImage && !heroImageUrl && !editingBlog?.heroImage) {
      showAlert("Please upload a hero image", "warning", "Required Fields");
      return;
    }

    setIsSubmitting(true);

    try {
      let finalImageUrl = heroImageUrl || editingBlog?.heroImage;
      
      if (heroImage) {
        try {
          finalImageUrl = await uploadImage();
        } catch (uploadError) {
          console.error("Image upload failed:", uploadError);
          showAlert(`Failed to upload image: ${uploadError.message || uploadError}\n\nPlease try again or use a direct image URL instead.`, "error", "Upload Failed");
          setIsSubmitting(false);
          return;
        }
      }

      const selectedSections = [];
      if (sections.commercial) selectedSections.push("commercial");
      if (sections.residential) selectedSections.push("residential");
      if (sections.industrial) selectedSections.push("industrial");

      // Get body content from editor if not set
      let finalBody = body;
      if (bodyEditorRef.current) {
        finalBody = bodyEditorRef.current.innerHTML || body;
      }
      
      // Ensure body is not empty
      if (!finalBody || finalBody.trim() === "" || finalBody === "<p>Start typing your blog content here...</p>") {
        showAlert("Please enter blog content", "warning", "Required Fields");
        setIsSubmitting(false);
        return;
      }

      // Content writers can only create unpublished blogs (for approval)
      // Admins can publish directly
      const canPublish = profile?.role === "admin";
      
      // For content writers: always set published to false (unless admin is editing and wants to keep published status)
      const publishedStatus = editingBlog 
        ? (canPublish ? editingBlog.published : false) // Content writers can't publish even when editing
        : canPublish; // New blogs: admins publish, content writers don't
      
      const blogData = {
        title: title.trim(),
        subtitle: subtitle.trim(),
        body: finalBody,
        excerpt: excerpt.trim() || subtitle.trim(),
        heroImage: finalImageUrl,
        sections: selectedSections,
        author: profile?.name || "Lunar Electric",
        createdAt: editingBlog?.createdAt || firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        published: publishedStatus,
        slug: editingBlog?.slug || generateSlug(title),
        pendingApproval: !canPublish, // Mark as pending if content writer
        sentBackForRevision: false, // Clear sent back flag when resubmitted
        draft: false // Clear draft flag when submitting for approval
      };
      
      // Debug logging for content writers
      if (profile?.role === "contentwriter") {
        console.log("Content Writer submitting blog:", {
          isEditing: !!editingBlog,
          canPublish,
          publishedStatus,
          author: blogData.author,
          profileName: profile?.name,
          blogData: {
            title: blogData.title,
            published: blogData.published,
            pendingApproval: blogData.pendingApproval
          }
        });
      }

      if (editingBlog) {
        await firestore.collection("blogs").doc(editingBlog.id).update(blogData);
      } else {
        await firestore.collection("blogs").add(blogData);
      }

      // Reset form
      setTitle("");
      setSubtitle("");
      setBody("");
      setExcerpt("");
      setHeroImage(null);
      setHeroImagePreview("");
      setHeroImageUrl("");
      setSections({ commercial: false, residential: false, industrial: false });
      setIsEditing(false);
      setEditingBlog(null);
      
      // IMPORTANT: Changes appear immediately via real-time listeners
      // NO REDEPLOYMENT NEEDED - Blog will appear on website instantly!
      if (canPublish) {
        showAlert(
          editingBlog 
            ? "Blog updated successfully! Changes are live on the website now." 
            : "Blog posted successfully! It's live on the website now.",
          "success",
          "Success"
        );
      } else {
        showAlert(
          editingBlog
            ? "Blog updated successfully! It has been sent for approval." 
            : "Blog submitted successfully! It has been sent for admin approval.",
          "success",
          "Submitted for Approval"
        );
      }
    } catch (error) {
      console.error("Error saving blog:", error);
      showAlert("Error saving blog. Please try again.", "error", "Error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleView = (blog) => {
    setViewingBlog(blog);
    setIsViewing(true);
    setIsEditing(false);
    window.scrollTo(0, 0);
  };

  const handleEdit = (blog) => {
    // Content writers can only edit their own unpublished blogs, drafts, or blogs sent back for revision
    if (profile?.role === "contentwriter" && blog.author !== profile?.name) {
      showAlert("You can only edit your own blog posts.", "warning", "Permission Denied");
      return;
    }
    if (profile?.role === "contentwriter" && blog.published && !blog.sentBackForRevision && !blog.draft) {
      showAlert("You cannot edit published blogs. Please create a new blog post.", "warning", "Cannot Edit");
      return;
    }
    
    setEditingBlog(blog);
    setTitle(blog.title);
    setSubtitle(blog.subtitle);
    setBody(blog.body || "");
    setExcerpt(blog.excerpt || blog.subtitle);
    setHeroImageUrl(blog.heroImage);
    setHeroImagePreview(blog.heroImage);
    setSections({
      commercial: blog.sections?.includes("commercial") || false,
      residential: blog.sections?.includes("residential") || false,
      industrial: blog.sections?.includes("industrial") || false
    });
    setIsEditing(true);
    setIsViewing(false);
    window.scrollTo(0, 0);
    // Set the editor content after a brief delay to ensure DOM is ready
    setTimeout(() => {
      if (bodyEditorRef.current) {
        bodyEditorRef.current.innerHTML = blog.body || "";
      }
    }, 100);
  };

  // Initialize editor content when editing existing blog
  useEffect(() => {
    if (isEditing && bodyEditorRef.current && editingBlog && body) {
      bodyEditorRef.current.innerHTML = body;
      // Ensure all headings have correct styles
      setTimeout(() => {
        ['h1', 'h2', 'h3', 'h4'].forEach(tag => {
          const headings = bodyEditorRef.current.querySelectorAll(tag);
          headings.forEach(heading => {
            const level = tag.replace('h', '');
            heading.style.fontSize = level === '1' ? '32px' : level === '2' ? '28px' : level === '3' ? '24px' : '20px';
            heading.style.fontWeight = (level === '1' || level === '2') ? '700' : '600';
            heading.style.color = '#1F3A93';
            heading.style.marginTop = level === '1' || level === '2' ? '2.5rem' : level === '3' ? '2rem' : '1.5rem';
            heading.style.marginBottom = level === '1' || level === '2' ? '1.5rem' : level === '3' ? '1rem' : '0.75rem';
            heading.style.lineHeight = '1.3';
            heading.style.fontFamily = "'Poppins', sans-serif";
          });
        });
        setBody(bodyEditorRef.current.innerHTML);
      }, 100);
    } else if (isEditing && bodyEditorRef.current && !editingBlog && !body) {
      bodyEditorRef.current.innerHTML = "";
    }
  }, [isEditing, editingBlog]);

  // Monitor for heading changes and apply styles
  useEffect(() => {
    if (!isEditing || !bodyEditorRef.current) return;
    
    const observer = new MutationObserver(() => {
      ['h1', 'h2', 'h3', 'h4'].forEach(tag => {
        const headings = bodyEditorRef.current.querySelectorAll(tag);
        headings.forEach(heading => {
          if (!heading.style.color || heading.style.color !== 'rgb(31, 58, 147)') {
            const level = tag.replace('h', '');
            heading.style.fontSize = level === '1' ? '32px' : level === '2' ? '28px' : level === '3' ? '24px' : '20px';
            heading.style.fontWeight = (level === '1' || level === '2') ? '700' : '600';
            heading.style.color = '#1F3A93';
            heading.style.marginTop = level === '1' || level === '2' ? '2.5rem' : level === '3' ? '2rem' : '1.5rem';
            heading.style.marginBottom = level === '1' || level === '2' ? '1.5rem' : level === '3' ? '1rem' : '0.75rem';
            heading.style.lineHeight = '1.3';
            heading.style.fontFamily = "'Poppins', sans-serif";
          }
        });
      });
    });
    
    observer.observe(bodyEditorRef.current, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, [isEditing]);

  // Check formatting state when selection changes
  const checkFormatState = () => {
    if (!bodyEditorRef.current) return;
    
    const selection = window.getSelection();
    if (selection.rangeCount === 0) {
      // No selection, use defaults
      setFormatState(prev => ({ ...prev, bold: false, italic: false, underline: false }));
      return;
    }
    
    const range = selection.getRangeAt(0);
    let element = null;
    
    if (range.collapsed) {
      // Check parent element formatting
      const container = range.commonAncestorContainer;
      element = container.nodeType === 3 ? container.parentElement : container;
    } else {
      // Check selected text formatting
      element = range.commonAncestorContainer.nodeType === 3 
        ? range.commonAncestorContainer.parentElement 
        : range.commonAncestorContainer;
    }
    
    if (element) {
      const computedStyle = window.getComputedStyle(element);
      const tagName = element.tagName ? element.tagName.toLowerCase() : '';
      const fontSize = computedStyle.fontSize || '16px';
      const textAlign = computedStyle.textAlign || 'left';
      const color = computedStyle.color || '#000000';
      
      // Extract RGB color and convert to hex if needed
      let hexColor = color;
      if (color.startsWith('rgb')) {
        const rgb = color.match(/\d+/g);
        if (rgb && rgb.length >= 3) {
          hexColor = '#' + rgb.map(x => {
            const hex = parseInt(x).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
          }).join('');
        }
      }
      
      setFormatState({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        fontSize: fontSize,
        textStyle: ['h1', 'h2', 'h3', 'h4', 'p'].includes(tagName) ? tagName : 'p',
        alignment: textAlign,
        color: hexColor
      });
    } else {
      setFormatState(prev => ({
        ...prev,
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline')
      }));
    }
  };

  // Update format state on selection change
  useEffect(() => {
    if (!isEditing || !bodyEditorRef.current) return;
    
    const editor = bodyEditorRef.current;
    const handleSelectionChange = () => {
      checkFormatState();
    };
    
    document.addEventListener('selectionchange', handleSelectionChange);
    editor.addEventListener('mouseup', handleSelectionChange);
    editor.addEventListener('keyup', handleSelectionChange);
    
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      editor.removeEventListener('mouseup', handleSelectionChange);
      editor.removeEventListener('keyup', handleSelectionChange);
    };
  }, [isEditing]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker]);


  const handleDelete = async (id) => {
    // Only admins can delete blogs
    if (profile?.role !== "admin") {
      showAlert("You don't have permission to delete blogs.", "warning", "Permission Denied");
      return;
    }
    
    showConfirm(
      "Are you sure you want to delete this blog? It will be removed from the website immediately.",
      async () => {
        try {
          await firestore.collection("blogs").doc(id).delete();
          // IMPORTANT: Deletion appears immediately via real-time listeners
          // NO REDEPLOYMENT NEEDED - Blog will be removed from website instantly!
          showAlert("Blog deleted successfully! It has been removed from the website.", "success", "Deleted");
        } catch (error) {
          console.error("Error deleting blog:", error);
          showAlert("Error deleting blog. Please try again.", "error", "Error");
        }
      },
      "Delete Blog",
      "warning"
    );
  };

  const handleNew = () => {
    setIsEditing(true);
    setEditingBlog(null);
    setTitle("");
    setSubtitle("");
    setBody("");
    setExcerpt("");
    setHeroImage(null);
    setHeroImagePreview("");
    setHeroImageUrl("");
    setSections({ commercial: false, residential: false, industrial: false });
    // Clear editor content - placeholder will show via CSS
    setTimeout(() => {
      if (bodyEditorRef.current) {
        bodyEditorRef.current.innerHTML = "";
      }
    }, 100);
    window.scrollTo(0, 0);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsViewing(false);
    setEditingBlog(null);
    setViewingBlog(null);
    setTitle("");
    setSubtitle("");
    setBody("");
    setExcerpt("");
    setHeroImage(null);
    setHeroImagePreview("");
    setHeroImageUrl("");
    setSections({ commercial: false, residential: false, industrial: false });
  };

  const handleCloseView = () => {
    setIsViewing(false);
    setViewingBlog(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString("en-GB", { month: "long" });
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  };


  return (
    <div className="quotation-system">
      <Helmet>
        <title>Blog Management - Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <TopBar />
      <Modal {...modal} onClose={hideModal} />
      <div className="elegant-container fade-in-up">
        <div className="elegant-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
            <div>
              <h1 className="elegant-title">
                {profile?.role === "admin" ? "Blog Management" : "Write Blog"}
              </h1>
              <p className="elegant-subtitle">
                {profile?.role === "admin" 
                  ? "Create and manage new blog posts - Changes appear immediately without redeployment"
                  : "Create blog posts and send them for admin approval"}
              </p>
            </div>
            {!isEditing && !isViewing && (
              <button
                onClick={handleNew}
                className="btn-elegant btn-elegant-accent"
                style={{ padding: "0.75rem 1.5rem", minHeight: "48px" }}
              >
                + New Blog
              </button>
            )}
          </div>

          {isViewing && viewingBlog && (
            <div style={{ marginBottom: "3rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
                <div>
                  <h2 style={{ fontSize: "1.5rem", fontWeight: "600", marginBottom: "0.5rem", color: "#1F3A93" }}>
                    {viewingBlog.title}
                  </h2>
                  <p style={{ fontSize: "1.125rem", color: "var(--neutral-600)", marginBottom: "1rem" }}>
                    {viewingBlog.subtitle}
                  </p>
                  <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", fontSize: "0.875rem", color: "var(--neutral-500)" }}>
                    <span>Author: {viewingBlog.author || "Lunar Electric"}</span>
                    <span>Created: {formatDate(viewingBlog.createdAt)}</span>
                    {viewingBlog.updatedAt && <span>Updated: {formatDate(viewingBlog.updatedAt)}</span>}
                    <span>
                      Status: {viewingBlog.published ? (
                        <span style={{ color: "#16a34a", fontWeight: "600" }}>Published</span>
                      ) : (
                        <span style={{ color: "#f59e0b", fontWeight: "600" }}>Pending Approval</span>
                      )}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleCloseView}
                  className="btn-elegant btn-elegant-secondary"
                  style={{ padding: "0.75rem 1.5rem", minHeight: "48px" }}
                >
                  Close
                </button>
              </div>

              {viewingBlog.heroImage && (
                <div style={{ marginBottom: "2rem" }}>
                  <img
                    src={viewingBlog.heroImage}
                    alt={viewingBlog.title}
                    style={{
                      width: "100%",
                      maxHeight: "400px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "2px solid var(--neutral-200)"
                    }}
                  />
                </div>
              )}

              {viewingBlog.sections && viewingBlog.sections.length > 0 && (
                <div style={{ marginBottom: "2rem" }}>
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    {viewingBlog.sections.map((section) => (
                      <span
                        key={section}
                        style={{
                          padding: "0.5rem 1rem",
                          borderRadius: "12px",
                          fontSize: "0.875rem",
                          backgroundColor: "var(--accent-cyan)",
                          color: "white",
                          textTransform: "capitalize"
                        }}
                      >
                        {section}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {viewingBlog.excerpt && (
                <div style={{ marginBottom: "2rem", padding: "1.5rem", backgroundColor: "var(--neutral-50)", borderRadius: "8px", border: "1px solid var(--neutral-200)" }}>
                  <h3 style={{ fontSize: "1rem", fontWeight: "600", marginBottom: "0.5rem", color: "#1F3A93" }}>Excerpt</h3>
                  <p style={{ fontSize: "1rem", lineHeight: "1.6", color: "var(--neutral-700)" }}>{viewingBlog.excerpt}</p>
                </div>
              )}

              <div style={{ marginBottom: "2rem" }}>
                <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "#1F3A93" }}>Blog Content</h3>
                <div
                  style={{
                    padding: "2rem",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    border: "1px solid var(--neutral-200)",
                    lineHeight: "1.8",
                    fontSize: "1rem",
                    color: "var(--neutral-800)"
                  }}
                  dangerouslySetInnerHTML={{ __html: viewingBlog.body || "<p>No content available.</p>" }}
                />
              </div>

              {profile?.role === "contentwriter" && !viewingBlog.published && (
                <div style={{ 
                  padding: "1.5rem", 
                  backgroundColor: "#fff9e6", 
                  borderRadius: "8px", 
                  border: "2px solid #f59e0b",
                  marginTop: "2rem"
                }}>
                  <p style={{ margin: 0, color: "#92400e", fontWeight: "500" }}>
                    ⚠️ This blog is pending admin approval. You can view it but cannot edit it at this time.
                  </p>
                </div>
              )}
            </div>
          )}

          {isEditing && (
            <form onSubmit={handleSubmit} style={{ marginBottom: "3rem" }}>
              <div style={{ marginBottom: "1.5rem" }}>
                <label className="form-label-elegant">Title *</label>
                <input
                  type="text"
                  className="form-input-elegant"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter blog title"
                  required
                  style={{ 
                    fontSize: "1.125rem", 
                    fontWeight: "600",
                    fontFamily: "'Poppins', sans-serif",
                    color: "#1F3A93"
                  }}
                />
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <label className="form-label-elegant">Subtitle *</label>
                <input
                  type="text"
                  className="form-input-elegant"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder="Enter blog subtitle"
                  required
                  style={{
                    fontSize: "18px",
                    lineHeight: "1.8",
                    color: "#4a5568",
                    fontFamily: "'Poppins', sans-serif"
                  }}
                />
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <label className="form-label-elegant">Excerpt (Optional - defaults to subtitle)</label>
                <textarea
                  className="form-input-elegant"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="Enter blog excerpt (shown in preview cards)"
                  rows="3"
                  style={{
                    fontSize: "16px",
                    lineHeight: "1.8",
                    color: "#4a5568",
                    fontFamily: "'Poppins', sans-serif"
                  }}
                />
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <label className="form-label-elegant">Hero Image *</label>
                <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    className="btn-elegant btn-elegant-secondary"
                    style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.75rem 1.5rem" }}
                  >
                    <FiUpload size={18} />
                    {heroImage || heroImageUrl ? "Change Image" : "Upload Image"}
                  </button>
                  {heroImagePreview && (
                    <img
                      src={heroImagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "150px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "2px solid var(--neutral-200)"
                      }}
                    />
                  )}
                </div>
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <label className="form-label-elegant">Sections * (Select at least one)</label>
                <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={sections.commercial}
                      onChange={(e) => setSections({ ...sections, commercial: e.target.checked })}
                      style={{ width: "20px", height: "20px", cursor: "pointer" }}
                    />
                    Commercial
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={sections.residential}
                      onChange={(e) => setSections({ ...sections, residential: e.target.checked })}
                      style={{ width: "20px", height: "20px", cursor: "pointer" }}
                    />
                    Residential
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={sections.industrial}
                      onChange={(e) => setSections({ ...sections, industrial: e.target.checked })}
                      style={{ width: "20px", height: "20px", cursor: "pointer" }}
                    />
                    Industrial
                  </label>
                </div>
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <label className="form-label-elegant">Body Content *</label>
                <div style={{ border: "1px solid var(--neutral-300)", borderRadius: "8px", overflow: "hidden" }}>
                  {/* Formatting Toolbar */}
                  <div 
                    style={{ 
                      display: "flex", 
                      gap: "0.5rem", 
                      padding: "0.75rem", 
                      borderBottom: "1px solid var(--neutral-300)",
                      backgroundColor: "var(--neutral-50)",
                      flexWrap: "wrap",
                      alignItems: "center"
                    }}
                  >
                    {/* Text Style */}
                    <select
                      value={formatState.textStyle || ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          if (e.target.value.startsWith('h')) {
                            const level = e.target.value.replace('h', '');
                            formatHeading(level);
                          } else {
                            formatText('formatBlock', e.target.value);
                          }
                        }
                      }}
                      style={{ 
                        padding: "0.5rem", 
                        borderRadius: "4px", 
                        border: "1px solid var(--neutral-300)",
                        backgroundColor: "white",
                        cursor: "pointer"
                      }}
                      title="Text Style"
                    >
                      <option value="">Style</option>
                      <option value="p">Paragraph</option>
                      <option value="h1">Heading 1</option>
                      <option value="h2">Heading 2</option>
                      <option value="h3">Heading 3</option>
                      <option value="h4">Heading 4</option>
                    </select>

                    {/* Font Size */}
                    <select
                      value={formatState.fontSize}
                      onChange={(e) => {
                        if (e.target.value) {
                          formatFontSize(e.target.value);
                        }
                      }}
                      style={{ 
                        padding: "0.5rem", 
                        borderRadius: "4px", 
                        border: "1px solid var(--neutral-300)",
                        backgroundColor: "white",
                        cursor: "pointer"
                      }}
                      title="Font Size"
                    >
                      <option value="12px">12px</option>
                      <option value="14px">14px</option>
                      <option value="16px">16px</option>
                      <option value="18px">18px</option>
                      <option value="20px">20px</option>
                      <option value="24px">24px</option>
                    </select>

                    {/* Text Formatting */}
                    <button
                      type="button"
                      onClick={() => formatText("bold")}
                      className="btn-elegant btn-elegant-secondary"
                      style={{ 
                        padding: "0.5rem", 
                        minWidth: "40px", 
                        minHeight: "40px",
                        backgroundColor: formatState.bold ? "#1F3A93" : "white",
                        color: formatState.bold ? "white" : "inherit"
                      }}
                      title="Bold (Ctrl+B)"
                    >
                      <FiBold size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => formatText("italic")}
                      className="btn-elegant btn-elegant-secondary"
                      style={{ 
                        padding: "0.5rem", 
                        minWidth: "40px", 
                        minHeight: "40px",
                        backgroundColor: formatState.italic ? "#1F3A93" : "white",
                        color: formatState.italic ? "white" : "inherit"
                      }}
                      title="Italic (Ctrl+I)"
                    >
                      <FiItalic size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => formatText("underline")}
                      className="btn-elegant btn-elegant-secondary"
                      style={{ 
                        padding: "0.5rem", 
                        minWidth: "40px", 
                        minHeight: "40px",
                        backgroundColor: formatState.underline ? "#1F3A93" : "white",
                        color: formatState.underline ? "white" : "inherit"
                      }}
                      title="Underline (Ctrl+U)"
                    >
                      <FiUnderline size={16} />
                    </button>

                    {/* Text Color with Presets */}
                    <div style={{ position: "relative", display: "inline-block" }} ref={colorPickerRef}>
                      <button
                        type="button"
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        className="btn-elegant btn-elegant-secondary"
                        style={{ 
                          padding: "0.5rem", 
                          minWidth: "40px", 
                          minHeight: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative"
                        }}
                        title="Text Color"
                      >
                        <span style={{ 
                          width: "20px", 
                          height: "20px", 
                          backgroundColor: formatState.color,
                          border: "1px solid #ccc",
                          borderRadius: "3px",
                          display: "block"
                        }} />
                      </button>
                      
                      {showColorPicker && (
                        <div style={{
                          position: "absolute",
                          top: "100%",
                          left: "0",
                          marginTop: "0.5rem",
                          backgroundColor: "white",
                          border: "1px solid var(--neutral-300)",
                          borderRadius: "8px",
                          padding: "0.75rem",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          zIndex: 1000,
                          minWidth: "200px"
                        }}>
                          <div style={{ marginBottom: "0.75rem", fontSize: "0.875rem", fontWeight: "600", color: "#333" }}>
                            Preset Colors
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.5rem", marginBottom: "0.75rem" }}>
                            {[
                              { name: "Black", value: "#000000" },
                              { name: "Primary Blue", value: "#1F3A93" },
                              { name: "Blue", value: "#2563eb" },
                              { name: "Light Blue", value: "#3B82F6" },
                              { name: "Dark Gray", value: "#4a5568" },
                              { name: "Gray", value: "#64748b" },
                              { name: "Red", value: "#dc2626" },
                              { name: "Green", value: "#16a34a" }
                            ].map((color) => (
                              <button
                                key={color.value}
                                type="button"
                                onClick={() => {
                                  formatColor(color.value);
                                  setShowColorPicker(false);
                                }}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  backgroundColor: color.value,
                                  border: "2px solid " + (formatState.color === color.value ? "#1F3A93" : "#e5e7eb"),
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  boxShadow: formatState.color === color.value ? "0 0 0 2px rgba(31, 58, 147, 0.2)" : "none"
                                }}
                                title={color.name}
                              />
                            ))}
                          </div>
                          <div style={{ borderTop: "1px solid var(--neutral-300)", paddingTop: "0.75rem" }}>
                            <div style={{ marginBottom: "0.5rem", fontSize: "0.875rem", fontWeight: "600", color: "#333" }}>
                              Custom Color
                            </div>
                            <input
                              type="color"
                              value={formatState.color}
                              onChange={(e) => {
                                formatColor(e.target.value);
                                setShowColorPicker(false);
                              }}
                              style={{ 
                                width: "100%", 
                                height: "40px", 
                                border: "1px solid var(--neutral-300)",
                                borderRadius: "4px",
                                cursor: "pointer"
                              }}
                              title="Custom Text Color"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Alignment */}
                    <button
                      type="button"
                      onClick={() => formatText("justifyLeft")}
                      className="btn-elegant btn-elegant-secondary"
                      style={{ 
                        padding: "0.5rem", 
                        minWidth: "40px", 
                        minHeight: "40px",
                        backgroundColor: formatState.alignment === 'left' ? "#1F3A93" : "white",
                        color: formatState.alignment === 'left' ? "white" : "inherit"
                      }}
                      title="Align Left"
                    >
                      <FiAlignLeft size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => formatText("justifyCenter")}
                      className="btn-elegant btn-elegant-secondary"
                      style={{ 
                        padding: "0.5rem", 
                        minWidth: "40px", 
                        minHeight: "40px",
                        backgroundColor: formatState.alignment === 'center' ? "#1F3A93" : "white",
                        color: formatState.alignment === 'center' ? "white" : "inherit"
                      }}
                      title="Align Center"
                    >
                      <FiAlignCenter size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => formatText("justifyRight")}
                      className="btn-elegant btn-elegant-secondary"
                      style={{ 
                        padding: "0.5rem", 
                        minWidth: "40px", 
                        minHeight: "40px",
                        backgroundColor: formatState.alignment === 'right' ? "#1F3A93" : "white",
                        color: formatState.alignment === 'right' ? "white" : "inherit"
                      }}
                      title="Align Right"
                    >
                      <FiAlignRight size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => formatText("justifyFull")}
                      className="btn-elegant btn-elegant-secondary"
                      style={{ 
                        padding: "0.5rem", 
                        minWidth: "40px", 
                        minHeight: "40px",
                        backgroundColor: formatState.alignment === 'justify' ? "#1F3A93" : "white",
                        color: formatState.alignment === 'justify' ? "white" : "inherit"
                      }}
                      title="Justify"
                    >
                      <span style={{ fontSize: "14px", fontWeight: "bold" }}>≡</span>
                    </button>

                    {/* Lists */}
                    <button
                      type="button"
                      onClick={() => {
                        formatText("insertUnorderedList");
                        // Ensure list styling is applied
                        setTimeout(() => {
                          const lists = bodyEditorRef.current?.querySelectorAll('ul');
                          lists?.forEach(ul => {
                            if (!ul.style.listStyleType) {
                              ul.style.listStyleType = 'disc';
                              ul.style.paddingLeft = '2rem';
                              ul.style.margin = '1rem 0';
                            }
                          });
                          setBody(bodyEditorRef.current?.innerHTML || '');
                        }, 50);
                      }}
                      className="btn-elegant btn-elegant-secondary"
                      style={{ padding: "0.5rem", minWidth: "40px", minHeight: "40px" }}
                      title="Bullet List"
                    >
                      <FiList size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        formatText("insertOrderedList");
                        // Ensure list styling is applied
                        setTimeout(() => {
                          const lists = bodyEditorRef.current?.querySelectorAll('ol');
                          lists?.forEach(ol => {
                            if (!ol.style.listStyleType) {
                              ol.style.listStyleType = 'decimal';
                              ol.style.paddingLeft = '2rem';
                              ol.style.margin = '1rem 0';
                            }
                          });
                          setBody(bodyEditorRef.current?.innerHTML || '');
                        }, 50);
                      }}
                      className="btn-elegant btn-elegant-secondary"
                      style={{ padding: "0.5rem", minWidth: "40px", minHeight: "40px" }}
                      title="Numbered List"
                    >
                      <FiType size={16} />
                    </button>

                    {/* Other Formatting */}
                    <button
                      type="button"
                      onClick={() => formatText("removeFormat")}
                      className="btn-elegant btn-elegant-secondary"
                      style={{ padding: "0.5rem 0.75rem", fontSize: "0.875rem" }}
                      title="Remove Formatting"
                    >
                      Clear Format
                    </button>
                  </div>
                  
                  {/* Editor */}
                  <div
                    ref={bodyEditorRef}
                    contentEditable
                    data-placeholder="Start typing your blog content here. Use the toolbar above to format your text. You can use headings, colors, lists, and more!"
                    onInput={(e) => {
                      const content = e.currentTarget.innerHTML;
                      setBody(content === '<br>' || content.trim() === '' ? '' : content);
                    }}
                    onBlur={(e) => {
                      const content = e.currentTarget.innerHTML;
                      setBody(content === '<br>' || content.trim() === '' ? '' : content);
                    }}
                    onFocus={(e) => {
                      if (!e.currentTarget.innerHTML || e.currentTarget.innerHTML.trim() === '' || e.currentTarget.innerHTML === '<br>') {
                        e.currentTarget.innerHTML = '';
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      
                      // Get clipboard data as HTML and plain text
                      const htmlData = e.clipboardData.getData('text/html');
                      const textData = e.clipboardData.getData('text/plain');
                      
                      // Get current selection
                      const selection = window.getSelection();
                      if (!selection.rangeCount) return;
                      
                      const range = selection.getRangeAt(0);
                      range.deleteContents();
                      
                      // Create a temporary div to parse HTML
                      const tempDiv = document.createElement('div');
                      tempDiv.innerHTML = htmlData || textData;
                      
                      // Normalize all elements to use default font styles
                      const normalizeElement = (el) => {
                        if (el.nodeType === Node.TEXT_NODE) {
                          return el;
                        }
                        
                        // Preserve semantic tags but normalize styles
                        const tagName = el.tagName?.toLowerCase();
                        if (['p', 'div', 'span', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li'].includes(tagName)) {
                          const normalized = document.createElement(tagName);
                          
                          // Apply default styles based on element type
                          if (tagName === 'p' || tagName === 'div' || tagName === 'span') {
                            normalized.style.fontSize = '16px';
                            normalized.style.lineHeight = '1.8';
                            normalized.style.color = '#4a5568';
                            normalized.style.fontFamily = "'Poppins', sans-serif";
                            if (tagName === 'p') {
                              normalized.style.marginBottom = '1.25rem';
                            }
                          } else if (tagName === 'h1') {
                            normalized.style.fontSize = '32px';
                            normalized.style.fontWeight = '700';
                            normalized.style.color = '#1F3A93';
                            normalized.style.marginTop = '2.5rem';
                            normalized.style.marginBottom = '1.5rem';
                            normalized.style.lineHeight = '1.3';
                            normalized.style.fontFamily = "'Poppins', sans-serif";
                          } else if (tagName === 'h2') {
                            normalized.style.fontSize = '28px';
                            normalized.style.fontWeight = '700';
                            normalized.style.color = '#1F3A93';
                            normalized.style.marginTop = '2.5rem';
                            normalized.style.marginBottom = '1.5rem';
                            normalized.style.lineHeight = '1.3';
                            normalized.style.fontFamily = "'Poppins', sans-serif";
                          } else if (tagName === 'h3') {
                            normalized.style.fontSize = '24px';
                            normalized.style.fontWeight = '600';
                            normalized.style.color = '#1F3A93';
                            normalized.style.marginTop = '2rem';
                            normalized.style.marginBottom = '1rem';
                            normalized.style.lineHeight = '1.3';
                            normalized.style.fontFamily = "'Poppins', sans-serif";
                          } else if (tagName === 'h4') {
                            normalized.style.fontSize = '20px';
                            normalized.style.fontWeight = '600';
                            normalized.style.color = '#1F3A93';
                            normalized.style.marginTop = '1.5rem';
                            normalized.style.marginBottom = '0.75rem';
                            normalized.style.lineHeight = '1.3';
                            normalized.style.fontFamily = "'Poppins', sans-serif";
                          } else if (tagName === 'li') {
                            normalized.style.fontSize = '16px';
                            normalized.style.lineHeight = '1.8';
                            normalized.style.color = '#4a5568';
                            normalized.style.marginBottom = '0.75rem';
                            normalized.style.fontFamily = "'Poppins', sans-serif";
                          }
                          
                          // Copy attributes (like href for links)
                          Array.from(el.attributes).forEach(attr => {
                            if (attr.name !== 'style') {
                              normalized.setAttribute(attr.name, attr.value);
                            }
                          });
                          
                          // Recursively normalize children
                          Array.from(el.childNodes).forEach(child => {
                            const normalizedChild = normalizeElement(child);
                            if (normalizedChild) {
                              normalized.appendChild(normalizedChild);
                            }
                          });
                          
                          return normalized;
                        }
                        
                        // For other elements, convert to text
                        return document.createTextNode(el.textContent || '');
                      };
                      
                      // Process all nodes in the temp div
                      const fragment = document.createDocumentFragment();
                      Array.from(tempDiv.childNodes).forEach(node => {
                        const normalized = normalizeElement(node);
                        if (normalized) {
                          fragment.appendChild(normalized);
                        }
                      });
                      
                      // If no HTML content, create a paragraph with plain text
                      if (fragment.childNodes.length === 0) {
                        const p = document.createElement('p');
                        p.style.fontSize = '16px';
                        p.style.lineHeight = '1.8';
                        p.style.color = '#4a5568';
                        p.style.fontFamily = "'Poppins', sans-serif";
                        p.style.marginBottom = '1.25rem';
                        p.textContent = textData;
                        fragment.appendChild(p);
                      }
                      
                      range.insertNode(fragment);
                      range.setStartAfter(fragment.lastChild || fragment);
                      range.collapse(true);
                      selection.removeAllRanges();
                      selection.addRange(range);
                      
                      // Update body state
                      setTimeout(() => {
                        setBody(bodyEditorRef.current.innerHTML);
                      }, 10);
                    }}
                    style={{
                      minHeight: "400px",
                      padding: "1rem",
                      fontSize: "16px",
                      lineHeight: "1.8",
                      color: "#4a5568",
                      fontFamily: "'Poppins', sans-serif",
                      outline: "none",
                      backgroundColor: "white"
                    }}
                    suppressContentEditableWarning={true}
                    className="blog-editor"
                  />
                  <style>{`
                    .blog-editor {
                      font-family: 'Poppins', sans-serif !important;
                    }
                    .blog-editor p {
                      font-size: 16px !important;
                      line-height: 1.8 !important;
                      color: #4a5568 !important;
                      margin-bottom: 1.25rem !important;
                      font-family: 'Poppins', sans-serif !important;
                    }
                    .blog-editor h1 {
                      font-size: 32px !important;
                      font-weight: 700 !important;
                      color: #1F3A93 !important;
                      margin-top: 2.5rem !important;
                      margin-bottom: 1.5rem !important;
                      line-height: 1.3 !important;
                      font-family: 'Poppins', sans-serif !important;
                    }
                    .blog-editor h2 {
                      font-size: 28px !important;
                      font-weight: 700 !important;
                      color: #1F3A93 !important;
                      margin-top: 2.5rem !important;
                      margin-bottom: 1.5rem !important;
                      line-height: 1.3 !important;
                      font-family: 'Poppins', sans-serif !important;
                    }
                    .blog-editor h3 {
                      font-size: 24px !important;
                      font-weight: 600 !important;
                      color: #1F3A93 !important;
                      margin-top: 2rem !important;
                      margin-bottom: 1rem !important;
                      line-height: 1.3 !important;
                      font-family: 'Poppins', sans-serif !important;
                    }
                    .blog-editor h4 {
                      font-size: 20px !important;
                      font-weight: 600 !important;
                      color: #1F3A93 !important;
                      margin-top: 1.5rem !important;
                      margin-bottom: 0.75rem !important;
                      line-height: 1.3 !important;
                      font-family: 'Poppins', sans-serif !important;
                    }
                    .blog-editor ul {
                      list-style-type: disc;
                      padding-left: 2rem;
                      margin: 1.5rem 0;
                    }
                    .blog-editor ol {
                      list-style-type: decimal;
                      padding-left: 2rem;
                      margin: 1.5rem 0;
                    }
                    .blog-editor li {
                      font-size: 16px !important;
                      line-height: 1.8 !important;
                      color: #4a5568 !important;
                      margin-bottom: 0.75rem !important;
                      font-family: 'Poppins', sans-serif !important;
                    }
                    .blog-editor strong {
                      font-weight: 600 !important;
                      color: #1e293b !important;
                    }
                    .blog-editor * {
                      font-family: 'Poppins', sans-serif !important;
                    }
                  `}</style>
                </div>
              </div>

              {/* Action buttons at bottom */}
              <div style={{ display: "flex", gap: "1rem", marginTop: "2rem", paddingTop: "2rem", borderTop: "1px solid var(--neutral-300)" }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-elegant btn-elegant-secondary"
                  style={{ padding: "0.75rem 1.5rem", minHeight: "48px" }}
                >
                  Cancel
                </button>
                {profile?.role === "contentwriter" && (
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={isSavingDraft}
                    className="btn-elegant"
                    style={{ 
                      padding: "0.75rem 1.5rem", 
                      minHeight: "48px",
                      backgroundColor: "#64748b",
                      color: "white",
                      border: "none",
                      opacity: isSavingDraft ? 0.6 : 1,
                      cursor: isSavingDraft ? "not-allowed" : "pointer",
                      pointerEvents: isSavingDraft ? "none" : "auto"
                    }}
                  >
                    {isSavingDraft ? "Saving..." : "Save as Draft"}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-elegant btn-elegant-accent"
                  style={{ 
                    padding: "0.75rem 1.5rem", 
                    minHeight: "48px",
                    opacity: isSubmitting ? 0.6 : 1,
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    backgroundColor: isSubmitting ? "#94a3b8" : undefined,
                    pointerEvents: isSubmitting ? "none" : "auto"
                  }}
                >
                  {isSubmitting 
                    ? "Saving..." 
                    : editingBlog 
                      ? (editingBlog.draft 
                          ? (profile?.role === "admin" ? "Update Blog" : "Send for Approval")
                          : (profile?.role === "admin" ? "Update Blog" : "Update & Resubmit"))
                      : (profile?.role === "admin" ? "Publish Blog" : "Send for Approval")}
                </button>
              </div>
            </form>
          )}

          {!isEditing && !isViewing && (
            <div>
              {/* Blogs Sent Back Section (Admins) */}
              {profile?.role === "admin" && blogs.filter(b => !b.published && b.sentBackForRevision && !b.draft).length > 0 && (
                <div style={{ marginBottom: "2rem" }}>
                  <h2 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "#f59e0b" }}>
                    Blogs Sent Back ({blogs.filter(b => !b.published && b.sentBackForRevision && !b.draft).length})
                  </h2>
                  <div style={{ overflowX: "auto", marginBottom: "2rem" }}>
                    <table className="table-elegant">
                      <thead>
                        <tr>
                          <th align="left">Title</th>
                          <th align="left">Author</th>
                          <th align="left">Created</th>
                          <th align="left">Last Updated</th>
                          <th align="left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blogs.filter(b => !b.published && b.sentBackForRevision && !b.draft).map((blog) => (
                          <tr key={blog.id} style={{ backgroundColor: "#fff4e6" }}>
                            <td data-label="Title">
                              <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>{blog.title}</div>
                              <div style={{ fontSize: "0.875rem", color: "var(--neutral-500)" }}>{blog.subtitle}</div>
                              <div style={{ fontSize: "0.75rem", color: "#f59e0b", marginTop: "0.25rem", fontStyle: "italic" }}>
                                ⚠️ Sent back for revision
                              </div>
                            </td>
                            <td data-label="Author">{blog.author || "Lunar Electric"}</td>
                            <td data-label="Created">{formatDate(blog.createdAt)}</td>
                            <td data-label="Last Updated">
                              {blog.updatedAt ? formatDate(blog.updatedAt) : "—"}
                            </td>
                            <td data-label="Actions">
                              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                                <button
                                  onClick={() => handleView(blog)}
                                  className="btn-elegant btn-elegant-secondary"
                                  style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                                >
                                  View
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Drafts Section (Content Writers) */}
              {profile?.role === "contentwriter" && blogs.filter(b => b.draft && (b.author || "").trim().toLowerCase() === (profile?.name || "").trim().toLowerCase()).length > 0 && (
                <div style={{ marginBottom: "2rem" }}>
                  <h2 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "#64748b" }}>
                    Drafts ({blogs.filter(b => b.draft && (b.author || "").trim().toLowerCase() === (profile?.name || "").trim().toLowerCase()).length})
                  </h2>
                  <div style={{ overflowX: "auto", marginBottom: "2rem" }}>
                    <table className="table-elegant">
                      <thead>
                        <tr>
                          <th align="left">Title</th>
                          <th align="left">Created</th>
                          <th align="left">Last Updated</th>
                          <th align="left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blogs.filter(b => b.draft && (b.author || "").trim().toLowerCase() === (profile?.name || "").trim().toLowerCase()).map((blog) => (
                          <tr key={blog.id} style={{ backgroundColor: "#f8fafc" }}>
                            <td data-label="Title">
                              <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>{blog.title || "Untitled Draft"}</div>
                              <div style={{ fontSize: "0.875rem", color: "var(--neutral-500)" }}>{blog.subtitle || "No subtitle"}</div>
                            </td>
                            <td data-label="Created">{formatDate(blog.createdAt)}</td>
                            <td data-label="Last Updated">
                              {blog.updatedAt ? formatDate(blog.updatedAt) : "—"}
                            </td>
                            <td data-label="Actions">
                              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                                <button
                                  onClick={() => handleView(blog)}
                                  className="btn-elegant btn-elegant-secondary"
                                  style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                                >
                                  View
                                </button>
                                <button
                                  onClick={() => handleEdit(blog)}
                                  className="btn-elegant btn-elegant-accent"
                                  style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                                >
                                  Continue Editing
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Sent Back for Revision Section (Content Writers) */}
              {profile?.role === "contentwriter" && blogs.filter(b => !b.published && b.sentBackForRevision && !b.draft && (b.author || "").trim().toLowerCase() === (profile?.name || "").trim().toLowerCase()).length > 0 && (
                <div style={{ marginBottom: "2rem" }}>
                  <h2 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "#f59e0b" }}>
                    Sent Back for Revision ({blogs.filter(b => !b.published && b.sentBackForRevision && !b.draft && (b.author || "").trim().toLowerCase() === (profile?.name || "").trim().toLowerCase()).length})
                  </h2>
                  <div style={{ overflowX: "auto", marginBottom: "2rem" }}>
                    <table className="table-elegant">
                      <thead>
                        <tr>
                          <th align="left">Title</th>
                          <th align="left">Created</th>
                          <th align="left">Last Updated</th>
                          <th align="left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blogs.filter(b => !b.published && b.sentBackForRevision && !b.draft && (b.author || "").trim().toLowerCase() === (profile?.name || "").trim().toLowerCase()).map((blog) => (
                          <tr key={blog.id} style={{ backgroundColor: "#fff4e6" }}>
                            <td data-label="Title">
                              <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>{blog.title}</div>
                              <div style={{ fontSize: "0.875rem", color: "var(--neutral-500)" }}>{blog.subtitle}</div>
                              <div style={{ fontSize: "0.75rem", color: "#f59e0b", marginTop: "0.25rem", fontStyle: "italic" }}>
                                ⚠️ Sent back by admin for revision
                              </div>
                            </td>
                            <td data-label="Created">{formatDate(blog.createdAt)}</td>
                            <td data-label="Last Updated">
                              {blog.updatedAt ? formatDate(blog.updatedAt) : "—"}
                            </td>
                            <td data-label="Actions">
                              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                                <button
                                  onClick={() => handleView(blog)}
                                  className="btn-elegant btn-elegant-secondary"
                                  style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                                >
                                  View
                                </button>
                                <button
                                  onClick={() => handleEdit(blog)}
                                  className="btn-elegant btn-elegant-accent"
                                  style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                                >
                                  Edit & Resubmit
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Pending Approval Section */}
              <div style={{ marginBottom: "2rem" }}>
                <h2 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "#1F3A93" }}>
                  {profile?.role === "admin" 
                    ? `Pending Approval (${blogs.filter(b => !b.published && !b.draft && !b.sentBackForRevision).length})`
                    : `Pending Approval (${blogs.filter(b => !b.published && !b.sentBackForRevision && !b.draft && (b.author || "").trim().toLowerCase() === (profile?.name || "").trim().toLowerCase()).length})`}
                </h2>
                {(() => {
                  const pendingBlogs = profile?.role === "admin" 
                    ? blogs.filter(b => !b.published && !b.draft && !b.sentBackForRevision) // Admins see all unpublished blogs (excluding drafts and sent back - sent back are in separate section)
                    : blogs.filter(b => !b.published && !b.sentBackForRevision && !b.draft && (b.author || "").trim().toLowerCase() === (profile?.name || "").trim().toLowerCase()); // Content writers don't see sent back blogs or drafts here
                  
                  return pendingBlogs.length > 0 ? (
                    <div style={{ overflowX: "auto", marginBottom: "2rem" }}>
                      <table className="table-elegant">
                        <thead>
                          <tr>
                            <th align="left">Title</th>
                            {profile?.role === "admin" && <th align="left">Author</th>}
                            <th align="left">Created</th>
                            <th align="left">Last Updated</th>
                            <th align="left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingBlogs.map((blog) => (
                            <tr key={blog.id} style={{ backgroundColor: "#fff9e6" }}>
                              <td data-label="Title">
                                <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>{blog.title}</div>
                                <div style={{ fontSize: "0.875rem", color: "var(--neutral-500)" }}>{blog.subtitle}</div>
                                {blog.sentBackForRevision && profile?.role === "admin" && (
                                  <div style={{ fontSize: "0.75rem", color: "#f59e0b", marginTop: "0.25rem", fontStyle: "italic" }}>
                                    ⚠️ Sent back for revision
                                  </div>
                                )}
                              </td>
                              {profile?.role === "admin" && (
                                <td data-label="Author">{blog.author || "Lunar Electric"}</td>
                              )}
                              <td data-label="Created">{formatDate(blog.createdAt)}</td>
                              <td data-label="Last Updated">
                                {blog.updatedAt ? formatDate(blog.updatedAt) : "—"}
                              </td>
                              <td data-label="Actions">
                                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                                  <button
                                    onClick={() => handleView(blog)}
                                    className="btn-elegant btn-elegant-secondary"
                                    style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                                  >
                                    View
                                  </button>
                                  {profile?.role === "contentwriter" && (
                                    <button
                                      onClick={() => handleEdit(blog)}
                                      className="btn-elegant btn-elegant-secondary"
                                      style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                                    >
                                      Edit
                                    </button>
                                  )}
                                  {profile?.role === "admin" && (
                                    <button
                                      onClick={() => handleEdit(blog)}
                                      className="btn-elegant btn-elegant-accent"
                                      style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                                    >
                                      Review & Edit
                                    </button>
                                  )}
                                  {profile?.role === "admin" && (
                                    <>
                                      <button
                                        onClick={async () => {
                                          if (publishingBlogId) return; // Prevent double clicks
                                          setPublishingBlogId(blog.id);
                                          try {
                                            await firestore.collection("blogs").doc(blog.id).update({
                                              published: true,
                                              pendingApproval: false,
                                              updatedAt: firebase.firestore.FieldValue.serverTimestamp()
                                            });
                                            showAlert("Blog published successfully!", "success", "Published");
                                          } catch (error) {
                                            console.error("Error publishing blog:", error);
                                            showAlert("Error publishing blog. Please try again.", "error", "Error");
                                          } finally {
                                            setPublishingBlogId(null);
                                          }
                                        }}
                                        disabled={publishingBlogId === blog.id}
                                        className="btn-elegant btn-elegant-accent"
                                        style={{ 
                                          padding: "0.5rem 1rem", 
                                          fontSize: "0.875rem",
                                          opacity: publishingBlogId === blog.id ? 0.6 : 1,
                                          cursor: publishingBlogId === blog.id ? "not-allowed" : "pointer",
                                          backgroundColor: publishingBlogId === blog.id ? "#94a3b8" : undefined,
                                          pointerEvents: publishingBlogId === blog.id ? "none" : "auto"
                                        }}
                                      >
                                        {publishingBlogId === blog.id ? "Publishing..." : "Publish"}
                                      </button>
                                      <button
                                        onClick={async () => {
                                          if (sendingBackBlogId) return; // Prevent double clicks
                                          setSendingBackBlogId(blog.id);
                                          try {
                                            await firestore.collection("blogs").doc(blog.id).update({
                                              sentBackForRevision: true,
                                              updatedAt: firebase.firestore.FieldValue.serverTimestamp()
                                            });
                                            showAlert("Blog has been sent back to the content writer for revision.", "success", "Sent Back");
                                          } catch (error) {
                                            console.error("Error sending blog back:", error);
                                            showAlert("Error sending blog back. Please try again.", "error", "Error");
                                          } finally {
                                            setSendingBackBlogId(null);
                                          }
                                        }}
                                        disabled={sendingBackBlogId === blog.id}
                                        className="btn-elegant"
                                        style={{ 
                                          padding: "0.5rem 1rem", 
                                          fontSize: "0.875rem",
                                          backgroundColor: "#f59e0b",
                                          color: "white",
                                          border: "none",
                                          opacity: sendingBackBlogId === blog.id ? 0.6 : 1,
                                          cursor: sendingBackBlogId === blog.id ? "not-allowed" : "pointer",
                                          pointerEvents: sendingBackBlogId === blog.id ? "none" : "auto"
                                        }}
                                      >
                                        {sendingBackBlogId === blog.id ? "Sending..." : "Send Back"}
                                      </button>
                                      <button
                                        onClick={() => handleDelete(blog.id)}
                                        className="btn-elegant btn-elegant-danger"
                                        style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                                      >
                                        Reject
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={{ color: "var(--neutral-500)", fontStyle: "italic", marginBottom: "2rem" }}>
                      {profile?.role === "admin" 
                        ? "No blogs pending approval."
                        : "You don't have any blogs pending approval."}
                    </p>
                  );
                })()}
              </div>

              {/* Published Blogs Section */}
              <h2 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "#1F3A93" }}>
                {profile?.role === "admin" ? "Published Blogs" : "My Published Blogs"}
              </h2>
              <div style={{ overflowX: "auto" }}>
                <table className="table-elegant">
                  <thead>
                    <tr>
                      <th align="left">Title</th>
                      <th align="left">Sections</th>
                      {profile?.role === "admin" && <th align="left">Author</th>}
                      <th align="left">Status</th>
                      <th align="left">Created</th>
                      <th align="left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogs.filter(b => {
                      // Both admins and content writers see only published blogs in this section (excluding drafts)
                      // (pending blogs are shown in the Pending Approval section above)
                      if (b.draft) return false; // Exclude drafts
                      if (profile?.role === "admin") {
                        return b.published;
                      }
                      // Content writers see only their own published blogs
                      if (!b.published) return false;
                      const blogAuthor = (b.author || "").trim().toLowerCase();
                      const profileName = (profile?.name || "").trim().toLowerCase();
                      return blogAuthor === profileName;
                    }).map((blog) => (
                      <tr key={blog.id}>
                        <td data-label="Title">
                          <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>{blog.title}</div>
                          <div style={{ fontSize: "0.875rem", color: "var(--neutral-500)" }}>{blog.subtitle}</div>
                        </td>
                        <td data-label="Sections">
                          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                            {blog.sections?.map((section) => (
                              <span
                                key={section}
                                style={{
                                  padding: "0.25rem 0.75rem",
                                  borderRadius: "12px",
                                  fontSize: "0.75rem",
                                  backgroundColor: "var(--accent-cyan)",
                                  color: "white",
                                  textTransform: "capitalize"
                                }}
                              >
                                {section}
                              </span>
                            ))}
                          </div>
                        </td>
                        {profile?.role === "admin" && (
                          <td data-label="Author">{blog.author || "Lunar Electric"}</td>
                        )}
                        <td data-label="Status">
                          <span style={{ 
                            padding: "0.25rem 0.75rem", 
                            borderRadius: "12px", 
                            fontSize: "0.75rem",
                            backgroundColor: "#16a34a",
                            color: "white"
                          }}>
                            Published
                          </span>
                        </td>
                        <td data-label="Created">{formatDate(blog.createdAt)}</td>
                        <td data-label="Actions">
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              onClick={() => handleView(blog)}
                              className="btn-elegant btn-elegant-secondary"
                              style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleEdit(blog)}
                              className="btn-elegant btn-elegant-accent"
                              style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                            >
                              Edit
                            </button>
                            {profile?.role === "admin" && (
                              <button
                                onClick={() => handleDelete(blog.id)}
                                className="btn-elegant btn-elegant-danger"
                                style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {blogs.filter(b => {
                      if (b.draft) return false; // Exclude drafts
                      if (profile?.role === "admin") {
                        return b.published;
                      }
                      return b.published && b.author === profile?.name;
                    }).length === 0 && (
                      <tr>
                        <td colSpan={profile?.role === "admin" ? "6" : "5"} style={{ textAlign: "center", padding: "2rem", color: "var(--neutral-500)" }}>
                          {profile?.role === "admin" 
                            ? "No published blogs yet." 
                            : "You don't have any published blogs yet."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
